import { useLocation, useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  LayoutGrid,
  Shield,
  Settings,
  Share2,
  Bell,
  Activity,
  BookOpen,
  X,
} from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { usePermission } from '@/hooks/usePermission'
import { useAuth } from '@/modules/auth/AuthContext'
import { MediaAvatar } from '@/components/ui/media-avatar'

interface SidebarNavItem {
  path: string
  label: string
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>
}

interface SidebarProps {
  collapsed: boolean
  mobileOpen: boolean
  onMobileClose: () => void
}

const catalogItem: SidebarNavItem = { path: '/catalog', label: 'Проекты', icon: LayoutGrid }
const auditItem: SidebarNavItem = { path: '/audit', label: 'Аудит', icon: Shield }
const settingsItem: SidebarNavItem = { path: '/settings', label: 'Настройки', icon: Settings }
const adminItem: SidebarNavItem = { path: '/admin/users', label: 'Администрирование', icon: Shield }
const monitoringItem: SidebarNavItem = { path: '/monitoring', label: 'Мониторинг', icon: Activity }
const devItem: SidebarNavItem = { path: '/dev/components', label: 'Dev UI', icon: LayoutGrid }

export function Sidebar({ collapsed, mobileOpen, onMobileClose }: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const canAudit = usePermission('audit:list')
  const canAdmin = usePermission('admin:manage')
  const canReadUsers = usePermission('users:read')
  const canMonitor = usePermission('system:health:read') || canAdmin
  const { state } = useAuth()
  const user = state.status === 'authenticated' ? state.user : null

  const showAdmin = canAdmin || canReadUsers

  const primaryItems = [
    catalogItem,
  ]

  const systemItems = [
    settingsItem,
    ...(canMonitor ? [monitoringItem] : []),
    ...(canAudit ? [auditItem] : []),
    ...(showAdmin ? [adminItem] : []),
    ...(import.meta.env.DEV ? [devItem] : []),
  ]

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/')

  const linkClass = (path: string) =>
    cn(
      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer',
      'hover:bg-accent-soft hover:text-accent',
      isActive(path)
        ? 'bg-accent-soft text-accent border-l-2 border-accent rounded-l-none'
        : 'text-text-muted',
    )

  const handleNavigate = (path: string) => {
    navigate(path)
    onMobileClose()
  }

  const content = (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-4 h-14 border-b border-border shrink-0">
        <div className="flex items-center justify-center w-7 h-7 rounded-[7px] bg-gradient-to-br from-accent to-[#8aa4ff] text-white text-xs font-bold shrink-0">
          DH
        </div>
        {!collapsed && <span className="text-sm font-semibold text-text">Data Hub</span>}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        <div>
          <div className={cn('px-3 pb-1 text-xs font-medium text-text-muted uppercase tracking-wider', collapsed && 'sr-only')}>
            Проекты
          </div>
          <nav className="space-y-0.5">
            {primaryItems.map((item) => (
              <div
                key={item.path}
                className={linkClass(item.path)}
                onClick={() => handleNavigate(item.path)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleNavigate(item.path) }}
              >
                <item.icon className="w-4 h-4 shrink-0" strokeWidth={2} />
                {!collapsed && <span>{item.label}</span>}
              </div>
            ))}
          </nav>
        </div>

        <Separator className="my-2" />

        <div>
          <div className={cn('px-3 pb-1 pt-1 text-xs font-medium text-text-muted uppercase tracking-wider', collapsed && 'sr-only')}>
            Доступ
          </div>
          <nav className="space-y-0.5">
            <div
              className={linkClass('/shared')}
              onClick={() => handleNavigate('/shared')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') handleNavigate('/shared') }}
            >
              <Share2 className="w-4 h-4 shrink-0" strokeWidth={2} />
              {!collapsed && <span>Доступные мне</span>}
            </div>
            <div
              className={linkClass('/notifications')}
              onClick={() => handleNavigate('/notifications')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') handleNavigate('/notifications') }}
            >
              <Bell className="w-4 h-4 shrink-0" strokeWidth={2} />
              {!collapsed && <span>Уведомления</span>}
            </div>
          </nav>
        </div>

        <Separator className="my-2" />

        <div>
          <div className={cn('px-3 pb-1 pt-1 text-xs font-medium text-text-muted uppercase tracking-wider', collapsed && 'sr-only')}>
            Система
          </div>
          <nav className="space-y-0.5">
            {systemItems.map((item) => (
              <div
                key={item.path}
                className={linkClass(item.path)}
                onClick={() => handleNavigate(item.path)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleNavigate(item.path) }}
              >
                <item.icon className="w-4 h-4 shrink-0" strokeWidth={2} />
                {!collapsed && <span>{item.label}</span>}
              </div>
            ))}
          </nav>
        </div>

      </div>

      <div className="p-3 border-t border-border shrink-0">
        <div
          className={linkClass('/docs')}
          onClick={() => handleNavigate('/docs')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') handleNavigate('/docs') }}
        >
          <BookOpen className="w-4 h-4 shrink-0" strokeWidth={2} />
          {!collapsed && <span>Документация</span>}
        </div>
        <div className={cn('flex items-center gap-3 px-3 py-2 mt-1', collapsed && 'justify-center')}>
          <MediaAvatar
            photoUrl={user?.avatar_url}
            label={user?.full_name || user?.email || '?'}
            size="sm"
          />
          {!collapsed && user && (
            <div className="flex flex-col min-w-0">
              <span className="text-sm text-text truncate">{user.full_name || user.email}</span>
              <span className="text-xs text-text-muted truncate">{user.email}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <>
      <aside
        className={cn(
          'hidden md:flex flex-col border-r border-border bg-surface transition-all duration-200 shrink-0',
          collapsed ? 'w-16' : 'w-55',
        )}
      >
        {content}
      </aside>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={onMobileClose}
          />
          <aside className="relative w-70 max-w-[80vw] bg-surface border-r border-border flex flex-col">
            <button
              className="absolute top-3 right-3 p-1 text-text-muted hover:text-text"
              onClick={onMobileClose}
              aria-label="Закрыть меню"
            >
              <X className="w-5 h-5" strokeWidth={2} />
            </button>
            {content}
          </aside>
        </div>
      )}
    </>
  )
}
